import React, { useState, useEffect, useRef, useContext } from "react";
import { Container, Button, Input, Label, Row, Col, Alert, Spinner, UncontrolledTooltip } from "reactstrap";
import classnames from "classnames";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../css/ambulatoryVisit.module.scss";
import { useAmbulatoryVisitService } from "../api/useAmbulatoryVisitQuery";
import CommonFileProgress from "Components/Common/CommonFileUpload/CommonFileProgress";
import addVolunteerStyle from "../../../../modules/Volunteer/MasterVolunteer/css/volunteer.module.scss";
import SectionCard from "Components/Common/SectionCard";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import { formatDate, GetUserData, showToastMessage, parseLocalDate } from "common/service/CommonService";
import { AMBULATORY_VISIT_PATHS } from "Routes/paths";
import FullPageLoader from "Components/Common/FullPageLoader";
import { BreadcrumbContext } from "Layouts/Header";
import TruncatedText from "Components/Common/TruncatedText";
import Commonbadge from "../../../../Components/Common/Commonbadge";
import { DROPOUT_REASONS_OPTIONS, DROPOUT_TYPE_OPTIONS, BLOCK_REASONS_OPTIONS, loadCodelistsDetailId } from "api/modules/common.option";
import { useForm } from "react-hook-form";
import { useStudyDetailsService } from "hooks/useCommonQuery";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { activityColumns, eligibilityFields, getBlockDateFields, getBlockReasonFields, getBlockTypeFields, getDropoutFields, overallStatusFields } from "../helper/ambulatoryvisitconstants";
import { validations } from "validations/FormValidations";


interface AmbulatoryFile extends File {
  attachmenttype?: string | null;
  visitactivityid?: number;
}

const parseSizeToBytes = (sizeStr: any): number => {
  if (!sizeStr) return 0;
  if (typeof sizeStr === "number") return sizeStr;

  const str = String(sizeStr).toLowerCase().trim();
  const val = parseFloat(str);
  if (isNaN(val)) return 0;

  if (str.includes("gb")) return val * 1024 * 1024 * 1024;
  if (str.includes("mb")) return val * 1024 * 1024;
  if (str.includes("kb")) return val * 1024;
  return val;
};

const AmbulatorySubjectDetail: React.FC = () => {
  const navigate = useNavigate();
  const { visitId, subjectId } = useParams<{ visitId: string; subjectId: string }>();
  const studyid = Number(localStorage.getItem("activestudyid") || 1);
  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Ambulatory Visits", link: "/study/detail/ambulatory" },
      { text: "Visit Details", link: AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || "") },
      { text: "Subject Visit Activity " },
    ]);
    return () => setBreadcrumbItems?.([]);
  }, [setBreadcrumbItems, visitId]);

  const [visit, setVisit] = useState<any>(null);
  const [subject, setSubject] = useState<any>(null);

  const [activeActivityId, setActiveActivityId] = useState<number | null>(null);
  const [activityAttachments, setActivityAttachments] = useState<Record<number, {
    uploadedFiles: AmbulatoryFile[];
    existingAttachments: any[];
    deletedAttachmentIds: number[];
  }>>({});
  const [previewFile, setPreviewFile] = useState<AmbulatoryFile | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [comments, setComments] = useState("");

  const {
    control,
    setValue,
    reset,
    watch,
    getValues,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      status: "",
      dropouttype: "",
      aereferenceno: "",
      dropoutdate: "",
      studystage: "",
      effectivedate: "",
      dropoutreason: "",
      detailedreason: "",
      blocktype: "Temporary",
      blockstartdate: "",
      blockenddate: "",
      blockduration: "0 days",
      blockreason: "",
      detailedremarks: "",
      rescreeningeligibility: "",
    },
    mode: "onChange"
  });

  const statusValue = watch("status");
  const dropoutDateValue = watch("dropoutdate");
  const effectiveDateValue = watch("effectivedate");
  const dropoutTypeValue = watch("dropouttype");

  const blockTypeValue = watch("blocktype");
  const blockStartDateValue = watch("blockstartdate");
  const blockEndDateValue = watch("blockenddate");

  useEffect(() => {
    if (statusValue && subject && subject.status !== statusValue) {
      setSubject((prev: any) => ({ ...prev, status: statusValue }));
    }
  }, [statusValue, subject]);

  useEffect(() => {
    if (dropoutDateValue && effectiveDateValue) {
      const start = new Date(dropoutDateValue);
      const end = new Date(effectiveDateValue);
      if (end < start) {
        setValue("effectivedate", "", { shouldValidate: true });
      }
    }
  }, [dropoutDateValue, effectiveDateValue, setValue]);

  useEffect(() => {
    if (blockTypeValue === undefined || blockTypeValue === null) {
      setValue("blocktype", "Temporary")
    }
    if (blockTypeValue === "Permanent") {
      setValue("blockduration", "0");
      return;
    }

    if (!blockStartDateValue) {
      setValue("blockenddate", "", { shouldValidate: true });
      setValue("blockduration", "0 days");
      return;
    }

    if (blockStartDateValue && blockEndDateValue) {
      const start = new Date(blockStartDateValue);
      const end = new Date(blockEndDateValue);
      if (end < start) {
        setValue("blockenddate", "", { shouldValidate: true });
        setValue("blockduration", "0 days");
        return;
      }
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setValue("blockduration", diffDays > 0 ? `${diffDays} days` : "0 days");
    } else {
      setValue("blockduration", "0 days");
    }
  }, [blockStartDateValue, blockEndDateValue, blockTypeValue, setValue]);

  useEffect(() => {
    if (dropoutDateValue || isSubmitted) {
      trigger("dropoutdate");
    }
  }, [dropoutDateValue, isSubmitted, trigger]);

  useEffect(() => {
    if (effectiveDateValue || isSubmitted) {
      trigger("effectivedate");
    }
  }, [effectiveDateValue, isSubmitted, trigger]);

  useEffect(() => {
    if (blockStartDateValue || isSubmitted) {
      trigger("blockstartdate");
    }
  }, [blockStartDateValue, isSubmitted, trigger]);

  useEffect(() => {
    if (blockEndDateValue || isSubmitted) {
      trigger("blockenddate");
    }
  }, [blockEndDateValue, isSubmitted, trigger]);

  useEffect(() => {
    loadCodelistsDetailId([
      "DROPOUT_TYPE_OPTIONS",
      "DROPOUT_REASONS_OPTIONS",
      "BLOCK_REASONS_OPTIONS"
    ]).then((result) => {
      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdown options failed to load");
      }
    });
  }, []);
  const [activitiesExpanded, setActivitiesExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDeleteExisting = (activityId: number, attachmentId: number) => {
    setActivityAttachments((prev) => {
      const current = prev[activityId] || { uploadedFiles: [], existingAttachments: [], deletedAttachmentIds: [] };
      return {
        ...prev,
        [activityId]: {
          ...current,
          deletedAttachmentIds: [...current.deletedAttachmentIds, attachmentId],
          existingAttachments: current.existingAttachments.filter(
            (a) => Number(a.ambulatoryattachmentid || a.id) !== Number(attachmentId)
          ),
        },
      };
    });
  };

  const handlePreview = (file: any) => {
  

    const API_BASE_URL =  localStorage.getItem("baseUrl") || process.env.REACT_APP_BASE_URL
      const baseUrl = API_BASE_URL?.replace("/api", "") || "";

    // NEW FILE (File object)
    if (file instanceof File) {
      setPreviewFile(file);
      setPreviewFileUrl(null);
      setPreviewOpen(true);
      return;
    }

    // EXISTING FILE (API object)
    const path = file?.filepath || file?.path;

    if (!path) {
      console.error("Invalid file object:", file);
      return;
    }

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, activityId: number) => {
    const files = (Array.from(e.target.files || []) as AmbulatoryFile[]).map(f => {
      f.visitactivityid = activityId;
      return f;
    });
    if (files.length === 0) return;

    setActivityAttachments((prev) => {
      const totalExistingSize = Object.values(prev).reduce((sum, att) => {
        return sum + (att.existingAttachments?.reduce((s, f) => s + parseSizeToBytes(f.filesize || f.size || 0), 0) || 0);
      }, 0);
      const totalUploadedSize = Object.values(prev).reduce((sum, att) => {
        return sum + (att.uploadedFiles?.reduce((s, f) => s + f.size, 0) || 0);
      }, 0);
      const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
      const MAX_TOTAL_SIZE = 20 * 1024 * 1024;

      if (totalExistingSize + totalUploadedSize + incomingTotal > MAX_TOTAL_SIZE) {
        showToastMessage("error", "Total attachment size cannot exceed 20 MB.");
        return prev;
      }

      const current = prev[activityId] || { uploadedFiles: [], existingAttachments: [], deletedAttachmentIds: [] };
      return {
        ...prev,
        [activityId]: {
          ...current,
          uploadedFiles: [...current.uploadedFiles, ...files],
        },
      };
    });
    setActiveActivityId(null);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent, activityId: number) => {
    e.preventDefault();
    setIsDragging(false);
    const files = (Array.from(e.dataTransfer.files) as AmbulatoryFile[]).map(f => {
      f.visitactivityid = activityId;
      return f;
    });
    if (files.length === 0) return;

    setActivityAttachments((prev) => {
      const totalExistingSize = Object.values(prev).reduce((sum, att) => {
        return sum + (att.existingAttachments?.reduce((s, f) => s + parseSizeToBytes(f.filesize || f.size || 0), 0) || 0);
      }, 0);
      const totalUploadedSize = Object.values(prev).reduce((sum, att) => {
        return sum + (att.uploadedFiles?.reduce((s, f) => s + f.size, 0) || 0);
      }, 0);
      const incomingTotal = files.reduce((sum, f) => sum + f.size, 0);
      const MAX_TOTAL_SIZE = 20 * 1024 * 1024;

      if (totalExistingSize + totalUploadedSize + incomingTotal > MAX_TOTAL_SIZE) {
        showToastMessage("error", "Total attachment size cannot exceed 20 MB.");
        return prev;
      }

      const current = prev[activityId] || { uploadedFiles: [], existingAttachments: [], deletedAttachmentIds: [] };
      return {
        ...prev,
        [activityId]: {
          ...current,
          uploadedFiles: [...current.uploadedFiles, ...files],
        },
      };
    });
    setActiveActivityId(null);
  };

  const removeFile = (index: number, activityId: number) => {
    setActivityAttachments((prev) => {
      const current = prev[activityId] || { uploadedFiles: [], existingAttachments: [], deletedAttachmentIds: [] };
      return {
        ...prev,
        [activityId]: {
          ...current,
          uploadedFiles: current.uploadedFiles.filter((_, i) => i !== index),
        },
      };
    });
  };



  const { SelectdataAmbulatoryVisit, SelectAllAssignedSubjects, insertSubjectActivityResult } = useAmbulatoryVisitService({
    studyid,
    visitid: Number(visitId),
  });

  const { SelectQuery: StudySelectQuery } = useStudyDetailsService(
    undefined,
    undefined,
    studyid || undefined
  );
  const studyDetails = StudySelectQuery?.data?.data?.[0];

  const getRomanNumeral = (num: number): string => {
    const romanMap: { [key: number]: string } = {
      1: "I", 2: "II", 3: "III", 4: "IV", 5: "V", 6: "VI", 7: "VII", 8: "VIII", 9: "IX", 10: "X"
    };
    return romanMap[num] || String(num);
  };

  const periodsList = studyDetails?.periods || [];
  const studyStageOptions = periodsList.map((p: any) => ({
    label: `Period ${getRomanNumeral(Number(p.period_no))}`,
    value: `Period ${getRomanNumeral(Number(p.period_no))}`,
  }));

  useEffect(() => {
    if (SelectdataAmbulatoryVisit.data) {
      const visitRes = (SelectdataAmbulatoryVisit.data as any)?.data;
      const visitData = visitRes?.visit?.[0];
      setVisit({
        id: visitData?.visitid || visitId,
        visitNumber: visitData?.visitnumber || `Visit ${String(visitData?.visitid || visitId).padStart(2, "0")}`,
        visitName: visitData?.visitname || "--",
        scheduledDate: visitData?.scheduleddate ? new Date(visitData.scheduleddate).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) : "30 Jun 2026",
        visitStatus: visitData?.visitstatus || "scheduled",
      });

      if (SelectAllAssignedSubjects.data) {
        const subjectsList = (SelectAllAssignedSubjects.data as any)?.data || [];
        const foundSub = subjectsList.find((s: any) => String(s.subjectno || s.subjectId) === String(subjectId));
        if (foundSub) {
          const mappedActivities = (foundSub.activities || visitRes?.activities || []).map((act: any) => {
            const actId = act.activityid || act.id;
            const masterAct = (visitRes?.activities || []).find(
              (m: any) => Number(m.activityid || m.id) === Number(actId)
            );
            const isMandatory =
              act.ismandatory === true || act.ismandatory === 1 || String(act.ismandatory).toLowerCase() === "true" ||
              masterAct?.ismandatory === true || masterAct?.ismandatory === 1 || String(masterAct?.ismandatory).toLowerCase() === "true" ||
              act.mandatory === true || act.mandatory === 1 || String(act.mandatory).toLowerCase() === "true" ||
              masterAct?.mandatory === true || masterAct?.mandatory === 1 || String(masterAct?.mandatory).toLowerCase() === "true";

            return {
              id: actId,
              name: act.activityname || act.name,
              status: act.status || "Not Started",
              result: act.result || null,
              performedDate: act.performedDate || act.completedon || "",
              performedTime: act.performedTime || "",
              performedBy: act.performedBy || "",
              remarks: act.remarks || "",
              ismandatory: isMandatory
            };
          });

          setSubject({
            ...foundSub,
            subjectId: foundSub.subjectno || subjectId,
            initials: foundSub.initials || foundSub.subjectname?.slice(0, 2).toUpperCase() || "SJ",
            checkinTime: foundSub.checkintime || foundSub.checkinTime || "09:00 AM",
            status: foundSub.subjectvisitstatus || foundSub.status || "checked in",
            activities: mappedActivities
          });

          const attachmentsMap: Record<number, { uploadedFiles: AmbulatoryFile[], existingAttachments: any[], deletedAttachmentIds: number[] }> = {};
          mappedActivities.forEach((act: any) => {
            attachmentsMap[act.id] = {
              uploadedFiles: [],
              existingAttachments: act.attachments || [],
              deletedAttachmentIds: []
            };
          });
          if (foundSub.attachments && Array.isArray(foundSub.attachments)) {
            foundSub.attachments.forEach((att: any) => {
              const actId = Number(att.activityid || att.activityId);
              if (actId && attachmentsMap[actId]) {
                if (!attachmentsMap[actId].existingAttachments.some(e => e.ambulatoryattachmentid === att.ambulatoryattachmentid)) {
                  attachmentsMap[actId].existingAttachments.push(att);
                }
              }
            });
          }
          setActivityAttachments(attachmentsMap);
          setComments(foundSub.comments || foundSub.remarks || "");
          const rawStatus = (foundSub.subjectvisitstatus || foundSub.status || "").toLowerCase();
          const initStatus = ["completed", "dropped", "blocked"].includes(rawStatus) ? rawStatus : "";
          reset({
            status: initStatus,
            dropouttype: foundSub.dropouttype || "",
            aereferenceno: foundSub.adverseeventnumber || foundSub.aereferenceno || "",
            dropoutdate: foundSub.dropoutdate || "",
            studystage: foundSub.studystage || "",
            effectivedate: foundSub.effectivedate || "",
            dropoutreason: foundSub.dropoutreason || "",
            detailedreason: foundSub.detailedreason || foundSub.remarks || "",
            blockstartdate: foundSub.blockstartdate || foundSub.startdate || "",
            blockreason: foundSub.blockreason || "",
            detailedremarks: foundSub.detailedremarks || foundSub.remarks || "",
          });
        } else {
          const mappedActivities = (visitRes?.activities || []).map((act: any) => {
            const isMandatory =
              act.ismandatory === true || act.ismandatory === 1 || String(act.ismandatory).toLowerCase() === "true" ||
              act.mandatory === true || act.mandatory === 1 || String(act.mandatory).toLowerCase() === "true";
            return {
              id: act.activityid || act.id,
              name: act.activityname || act.name,
              status: "Not Started",
              result: null,
              performedDate: "",
              performedTime: "",
              performedBy: "",
              remarks: "",
              ismandatory: isMandatory
            };
          });
          setSubject({
            subjectId: subjectId,
            initials: "SJ",
            checkinTime: "09:00 AM",
            status: "checked in",
            activities: mappedActivities
          });
          const attachmentsMap: Record<number, { uploadedFiles: AmbulatoryFile[], existingAttachments: any[], deletedAttachmentIds: number[] }> = {};
          mappedActivities.forEach((act: any) => {
            attachmentsMap[act.id] = {
              uploadedFiles: [],
              existingAttachments: [],
              deletedAttachmentIds: []
            };
          });
          setActivityAttachments(attachmentsMap);
          setComments("");
          reset({
            status: "",
            dropouttype: "",
            aereferenceno: "",
            dropoutdate: "",
            studystage: "",
            effectivedate: "",
            dropoutreason: "",
            detailedreason: "",
            blockstartdate: "",
            blockreason: "",
            detailedremarks: "",
          });
        }
      }
    }
  }, [SelectdataAmbulatoryVisit.data, SelectAllAssignedSubjects.data, subjectId, visitId, reset]);

  const saveState = (updatedSubject: any) => {
    setSubject(updatedSubject);
  };

  const handleActivityFieldChange = (actId: number, field: string, value: any) => {
    if (!subject) return;
    const updatedActivities = subject.activities.map((act: any) => {
      if (act.id === actId) {
        return {
          ...act,
          [field]: value
        };
      }
      return act;
    });
    saveState({ ...subject, activities: updatedActivities });
  };

  const handleActivityResultChange = (actId: number, resultType: "Passed" | "Failed") => {
    if (!subject) return;
    const userData = GetUserData();
    const userName = userData ? `${userData.first_name || ""} ${userData.last_name || ""}`.trim() || userData.username || "Riya Patel" : "Riya Patel";

    const updatedActivities = subject.activities.map((act: any) => {
      if (act.id === actId) {
        const newResult = act.result === resultType ? null : resultType;
        const now = new Date();
        const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        const dateStr = now.toISOString().split('T')[0];

        return {
          ...act,
          result: newResult,
          status: newResult ? "completed" : "Not Started",
          performedDate: newResult ? (act.performedDate || dateStr) : "",
          performedTime: newResult ? (act.performedTime || timeStr) : "",
          performedBy: newResult ? (act.performedBy || userName) : ""
        };
      }
      return act;
    });
    saveState({ ...subject, activities: updatedActivities });
  };

  const handleSubmitSubjectDetail = async () => {
    // Validate custom activity remarks length
    const hasInvalidRemarks = subject?.activities?.some((act: any) => {
      const validationResult = validations.ambulatoryVisit.remarks.validate(act.remarks || "");
      return typeof validationResult === "string";
    });

    if (hasInvalidRemarks) {
      showToastMessage("error", "Please fix validation errors in activity remarks.");
      return;
    }

    const isValid = await trigger();
    if (!isValid) {
      setTimeout(() => {
        const errorEl = document.querySelector(".is-invalid, .invalid-feedback, .form-feedback");
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }

    const formValues = getValues();
    const currentStatus = formValues.status?.toLowerCase();

    const isCompletedOrCheckedOut =
      currentStatus === "completed" ||
      currentStatus === "checked in" ||
      currentStatus === "checked out" ||
      currentStatus === "activities completed";

    if (activeActivityId !== null) {
      const activeActName = subject?.activities?.find((a: any) => a.id === activeActivityId)?.name || "selected activity";
      showToastMessage(
        "error",
        `Please complete the file upload for "${activeActName}" or cancel the current selection before proceeding.`
      );
      return;
    }

    const payload = {
      subject,
      formValues,
      activityAttachments,
      comments,
      studyid,
      subjectId
    };

    insertSubjectActivityResult.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Subject activities and decision saved successfully!");
        navigate(AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || ""));
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || err || "Failed to save subject activities!");
      }
    });
  };

  if (SelectdataAmbulatoryVisit.isLoading || SelectAllAssignedSubjects.isLoading || StudySelectQuery?.isFetching || !subject || !visit) {
    return <FullPageLoader message="Loading Subject Details..." />;
  }

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>

        {/* Subject Header Card */}
        <SectionCard
          icon="ri-user-search-line"
          title="Subject Identification"
          subtitle="Details of the subject and active visit check-in status."
        >
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 w-100">
            <div className="d-flex align-items-center gap-3 w-100">
              <div className={`${styles.detailMetaGrid} flex-grow-1`}>
                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-user-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject No.</div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>{subject.subjectId}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-passport-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Number</div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>{visit.visitNumber}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-bookmark-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Name</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                      <TruncatedText text={visit.visitName || "--"} maxWidth="150px" />
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-calendar-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Date</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>{formatDate(visit.scheduledDate)}</div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-time-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Check-In</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                      {subject.checkintime ? subject.checkintime.split(" ")[0] : "09:00 AM"}
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-checkbox-circle-fill fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject Visit Status</div>
                    <Commonbadge status={subject?.subjctvisitstatus || "checked in"} style={{ width: "90px", fontSize: "12px" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Column Layout / Content Panels */}
        <div className="d-flex flex-column">

          {/* Card 1: Subject Activities Table */}
          <SectionCard
            icon="ri-checkbox-line"
            title="Subject Activities"
            subtitle="Mark the status of each activity performed during this visit."
            rightContent={
              subject.activities && subject.activities.length > 5 ? (
                <Button
                  color="primary"
                  size="sm"
                  outline
                  onClick={() => setActivitiesExpanded(!activitiesExpanded)}
                >
                  {activitiesExpanded ? (
                    <>
                      <i className="ri-arrow-up-s-line me-1" /> Collapse
                    </>
                  ) : (
                    <>
                      <i className="ri-arrow-down-s-line me-1" /> Expand All
                    </>
                  )}
                </Button>
              ) : undefined
            }
          >
            <CommonTableIndexWise
              columns={activityColumns(handleActivityResultChange, handleActivityFieldChange)}
              data={
                subject.activities
                  ? activitiesExpanded
                    ? subject.activities
                    : subject.activities.slice(0, 5)
                  : []
              }
              hideAction={true}
              paginationShow={false}
            />
          </SectionCard>

          {/* Card 2: Attachments & Verification */}
          <SectionCard
            icon="ri-attachment-2"
            title="Attachments & Verification"
            subtitle="Upload supporting documents specifically for each activity."
          >
            {subject?.activities && subject.activities.length > 0 ? (
              <div className="p-3 border rounded-3 bg-white">
                {/* 1. Styled Activity Selection Chips */}
                <div className="mb-3">
                  <Label className="fw-bold text-dark mb-2 font-size-13">
                    Select Activity to Attach Files
                  </Label>
                  <div className="d-flex flex-wrap gap-2">
                    {subject.activities.map((act: any) => {
                      const attachments = activityAttachments[act.id] || { uploadedFiles: [], existingAttachments: [] };
                      const totalFiles = (attachments.existingAttachments?.length || 0) + (attachments.uploadedFiles?.length || 0);
                      const hasFiles = totalFiles > 0;
                      const isActive = activeActivityId === act.id;

                      return (
                        <div
                          key={act.id}
                          onClick={() => {
                            if (isActive) {
                              setActiveActivityId(null);
                            } else {
                              setActiveActivityId(act.id);
                            }
                          }}
                          style={{
                            padding: '6px 16px',
                            borderRadius: '30px',
                            border: `1.5px solid ${isActive ? '#2E86AB' : (hasFiles ? '#10B981' : '#E5E7EB')}`,
                            backgroundColor: isActive ? '#F0F7FF' : (hasFiles ? '#F0FDF4' : '#FFF'),
                            color: isActive ? '#2E86AB' : (hasFiles ? '#10B981' : '#4B5563'),
                            cursor: 'pointer',
                            fontWeight: isActive || hasFiles ? '600' : '400',
                            fontSize: '12px',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            boxShadow: isActive ? '0 3px 8px rgba(46, 134, 171, 0.12)' : 'none',
                          }}
                        >
                          <i className={`${hasFiles ? 'ri-checkbox-circle-fill' : (isActive ? 'ri-checkbox-circle-line' : 'ri-add-circle-line')} me-1`} style={{ fontSize: '14px' }} />
                          <TruncatedText text={act.name || "--"} maxWidth="150px" />
                          {/* {act.ismandatory && <span className="text-danger ms-1">*</span>} */}
                          {totalFiles > 0 && (
                            <span className={classnames("badge rounded-pill font-size-10 ms-1 px-2 py-0.5", {
                              "bg-primary text-white": isActive,
                              "bg-success text-white": !isActive
                            })}>
                              {totalFiles}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 2. File Upload Zone */}
                {(() => {
                  const activeActivity = subject?.activities?.find((a: any) => a.id === activeActivityId);
                  const activeAttachments = activeActivity ? (activityAttachments[activeActivity.id] || { uploadedFiles: [], existingAttachments: [] }) : null;

                  const filesToShow = Object.values(activityAttachments).flatMap(att => att.uploadedFiles);

                  const existingSizeBytes = Object.values(activityAttachments).reduce(
                    (sum, att) => sum + att.existingAttachments.reduce((s, f) => s + parseSizeToBytes(f.filesize || f.size || 0), 0),
                    0
                  );

                  return (
                    <div className="mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="form-label text-dark small fw-medium mb-0">
                          Upload Documents (PDF - Max 20MB)
                        </label>
                        {activeActivity && activeActivity.ismandatory && (
                          <span className="badge bg-danger-subtle text-danger font-size-10">Mandatory Activity</span>
                        )}
                      </div>

                      <CommonFileProgress
                        files={filesToShow}
                        existingSizeBytes={existingSizeBytes}
                        maxSizeMB={20}
                      />

                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                          if (activeActivityId !== null) setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (activeActivityId === null) return;
                          handleDrop(e, activeActivityId);
                        }}
                        onClick={() => {
                          if (activeActivityId === null) return;
                          fileInputRef.current?.click();
                        }}
                        style={{
                          border: '1px dashed ',
                          borderColor: '#9d9ea0ff',
                          padding: '30px 20px',
                          cursor: activeActivityId === null ? 'not-allowed' : 'pointer',
                          background: activeActivityId === null ? '#f8f9fa' : (isDragging ? '#f0f7ff' : '#fafbfc'),
                          opacity: activeActivityId === null ? 0.6 : 1,
                          transition: 'all 0.2s',
                          borderRadius: 8,
                          textAlign: "center"
                        }}
                      >
                        <div
                          className="mx-auto mb-2 d-flex align-items-center justify-content-center rounded-circle"
                          style={{ width: '48px', height: '48px', backgroundColor: '#F0F7FF', color: '#2E86AB' }}
                        >
                          <i className="ri-upload-cloud-2-line" style={{ fontSize: '24px' }} />
                        </div>
                        <h6 className="fw-bold mb-1 font-size-13">Click to upload or drag and drop</h6>
                        <p className="text-muted small mb-0 font-size-11 d-flex align-items-center justify-content-center gap-1">
                          {activeActivityId === null ? (
                            "Select an activity above to enable upload"
                          ) : (
                            <>
                              <span>Upload document for</span>
                              <TruncatedText text={activeActivity.name || "--"} maxWidth="150px" />
                            </>
                          )}
                        </p>
                        <p className="text-muted font-size-10 mb-0">Maximum file size 20 MB</p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple={true}
                          accept=".pdf"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            if (activeActivityId !== null) {
                              handleFileChange(e, activeActivityId);
                            }
                          }}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* 3. Render file lists - always show all attachments grouped by activity */}
                <div className="mt-3">
                  {(() => {
                    const allAttachments: any[] = [];
                    subject.activities.forEach((act: any) => {
                      const attachments = activityAttachments[act.id] || { uploadedFiles: [], existingAttachments: [] };

                      attachments.existingAttachments.forEach((file: any) => {
                        allAttachments.push({
                          type: "existing",
                          file,
                          activityId: act.id,
                          activityName: act.name,
                        });
                      });

                      attachments.uploadedFiles.forEach((file: any, idx: number) => {
                        allAttachments.push({
                          type: "new",
                          file,
                          fileIndex: idx,
                          activityId: act.id,
                          activityName: act.name,
                        });
                      });
                    });

                    if (allAttachments.length === 0) return null;

                    return (
                      <Row className="g-3 w-100 mx-0">
                        {allAttachments.map((item, idx) => {
                          const { type, file, activityId, activityName, fileIndex } = item;
                          const fileName = type === "existing" ? (file.filename || file.name) : file.name;
                          const isActive = activeActivityId === activityId;

                          return (
                            <Col
                              key={`${type}-${activityId}-${idx}`}
                              md={3}
                              sm={12}
                            >
                              <div className={`${addVolunteerStyle.attachmentCard} h-100 shadow-sm border ${isActive ? "border-primary" : ""}`} style={{ outline: isActive ? "1px solid #2E86AB" : "none" }}>
                                <div className={addVolunteerStyle.fileInfo}>
                                  <div className={addVolunteerStyle.fileIcon}>
                                    <i className="ri-file-pdf-line text-danger" />
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className={addVolunteerStyle.fileName}>
                                      <TruncatedText text={fileName} />
                                    </div>
                                    <div className={addVolunteerStyle.fileMeta} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                      <span className="text-muted font-size-10">Document</span>
                                      <span className="text-muted font-size-10">•</span>
                                      <span className="text-primary font-size-10 fw-semibold">
                                        <TruncatedText text={activityName || "--"} maxWidth="100px" />
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className={addVolunteerStyle.fileActions}>
                                  <i
                                    className="ri-eye-line text-primary"
                                    title="Preview"
                                    style={{ cursor: "pointer", marginRight: "10px" }}
                                    onClick={() => handlePreview(file)}
                                  />
                                  <i
                                    className="ri-delete-bin-line text-danger"
                                    title="Delete"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      if (type === "existing") {
                                        handleDeleteExisting(activityId, file.ambulatoryattachmentid || file.id);
                                      } else {
                                        removeFile(fileIndex, activityId);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-muted font-size-13">No activities configured.</div>
            )}
          </SectionCard>

          {/* Card 3: Overall Decision */}
          <SectionCard
            icon="ri-shield-check-line"
            title="Overall Decision"
            subtitle="Record the overall decision or action applicable for this subject for this visit."
          >
            <div className="mb-3 pb-1 border-bottom">
              <CommonForm
                fields={overallStatusFields}
                control={control}
                errors={errors}
                columns={1}
                setValue={setValue}
              />
            </div>

            {/* Dropped (Discontinued) Fields */}
            {(subject.status?.toLowerCase() === "dropped") && (() => {
              const fields = getDropoutFields(dropoutDateValue, dropoutTypeValue, studyStageOptions);
              const selectedType = DROPOUT_TYPE_OPTIONS.find(
                (opt) => String(opt.value) === String(dropoutTypeValue) || opt.label === dropoutTypeValue
              )?.label || (dropoutTypeValue ? String(dropoutTypeValue) : null);
              const isAE = selectedType === "Medical / AE";
              return (
                <div className="p-3 border rounded bg-light-subtle mb-4 shadow-sm">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2 font-size-13">
                    <i className="ri-logout-box-r-line me-2 text-warning" />
                    Dropout Details
                  </h6>

                  {isAE && (
                    <div className="mt-2 mb-3">
                      <Alert color="danger" className="border-0 bg-light-danger text-danger d-flex flex-column gap-1 p-3">
                        <div className="fw-semibold">
                          <i className="ri-error-warning-line me-2" />
                          Medical / Adverse Event Dropout Selected
                        </div>
                        <div className="small text-muted">
                          Please provide the linked AE reference number for traceability.
                        </div>
                      </Alert>
                    </div>
                  )}

                  <CommonForm
                    fields={isAE ? [fields[0], fields[2], fields[3]] : [fields[0], fields[1], fields[2]]}
                    control={control}
                    errors={errors}
                    columns={3}
                    setValue={setValue}
                  />
                  <div className="mt-3">
                    <CommonForm
                      fields={isAE ? [fields[1], fields[4], fields[5]] : [fields[3], fields[4]]}
                      control={control}
                      errors={errors}
                      columns={3}
                      setValue={setValue}
                    />
                  </div>
                  <div className="mt-3">
                    <CommonForm
                      fields={isAE ? [fields[6]] : [fields[5]]}
                      control={control}
                      errors={errors}
                      columns={1}
                      setValue={setValue}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Blocked Fields */}
            {subject.status?.toLowerCase() === "blocked" && (
              <div className="p-3 border rounded bg-light-subtle mb-4 shadow-sm">
                <h6 className="fw-bold text-dark mb-3 border-bottom pb-2 font-size-13">
                  <i className="ri-forbid-2-line me-2 text-danger" />
                  Block Subject Details
                </h6>
                <div className="mb-3">
                  <CommonForm
                    fields={getBlockTypeFields(BLOCK_REASONS_OPTIONS)}
                    control={control}
                    errors={errors}
                    columns={1}
                    setValue={setValue}
                  />
                </div>

                {blockTypeValue === "Temporary" ? (
                  <>
                    <div className="mb-3">
                      <CommonForm
                        fields={getBlockDateFields(blockTypeValue, blockStartDateValue)}
                        control={control}
                        errors={errors}
                        columns={3}
                        setValue={setValue}
                      />
                    </div>
                    <div className="mb-3">
                      <CommonForm
                        fields={[getBlockReasonFields()[0]]} // blockreason
                        control={control}
                        errors={errors}
                        columns={1}
                        setValue={setValue}
                      />
                    </div>
                    <div className="mb-3">
                      <CommonForm
                        fields={[getBlockReasonFields()[1]]} // detailedremarks
                        control={control}
                        errors={errors}
                        columns={1}
                        setValue={setValue}
                      />
                    </div>
                    <div className="mb-3">
                      <CommonForm
                        fields={eligibilityFields}
                        control={control}
                        errors={errors}
                        columns={1}
                        setValue={setValue}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3">
                      <CommonForm
                        fields={[
                          getBlockDateFields(blockTypeValue, blockStartDateValue)[0], // blockstartdate
                          getBlockReasonFields()[0], // blockreason
                        ]}
                        control={control}
                        errors={errors}
                        columns={2}
                        setValue={setValue}
                      />
                    </div>
                    <div className="mb-3">
                      <CommonForm
                        fields={[getBlockReasonFields()[1]]} // detailedremarks
                        control={control}
                        errors={errors}
                        columns={1}
                        setValue={setValue}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Comments */}
            <Row>
              <Col>
                <label className="form-label text-dark small fw-bold">
                  Additional Comments / Discrepancies
                  <i
                    className="ri-information-line text-muted ms-1 cursor-pointer"
                    id="comments-tooltip"
                    style={{ cursor: "pointer" }}
                  />
                  <UncontrolledTooltip target="comments-tooltip" placement="top">
                    Additional Comments / Discrepancies (max 255 characters)
                  </UncontrolledTooltip>
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter any notes, observations, or discrepancy details..."
                  value={comments}
                  maxLength={255}
                  onChange={(e) => setComments(e.target.value)}
                  style={{ fontSize: 13, resize: "none", textAlign: "justify" }}
                />
                <small className="text-muted d-block mt-1 text-end">
                  {comments.length} / 255 characters
                </small>
              </Col>
            </Row>
          </SectionCard>

        </div>
      </Container>

      {/* Sticky Footer */}
      <div className={formStyles.stickyFooter}>
        <div className="px-3">
          <div className={formStyles.footerActions}>
            <Button
              type="button"
              color="dark"
              outline
              onClick={() => navigate(AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || ""))
              }
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={insertSubjectActivityResult.isPending}
              onClick={handleSubmitSubjectDetail}
            >
              {insertSubjectActivityResult.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" /> Submit
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>
      </div>
      {/* File Preview Modal */}
      <FilePreviewModal
        file={previewFile}
        fileUrl={previewFileUrl}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
};

export default AmbulatorySubjectDetail;
